import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShareIcon } from '@heroicons/react/24/outline';
import type { ProjectGraphNode, ProjectGraphNodeKind } from '@avisoma/shared';
import { PageHeader } from '../../components/PageHeader';
import { TableSkeleton } from '../../components/Skeleton';
import { serializeCsv } from '../../components/csv';
import { downloadText } from '../../components/ExportAsCSVButton';
import { formatShortId } from '../../components/ID';
import { Link } from '../../components/Link';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { emptyState } from './emptyState';
import { FocusPicker } from './FocusPicker';
import { GraphCanvas, type GraphCanvasHandle, type ViewportRequest } from './GraphCanvas';
import { GraphAudit } from './GraphAudit';
import { GraphEmptyState } from './GraphEmptyState';
import { GeneratedAt } from './GeneratedAt';
import { GraphFilters } from './GraphFilters';
import { GraphLoadError } from './GraphLoadError';
import { GraphSearch } from './GraphSearch';
import { GraphTaskMenu } from './GraphTaskMenu';
import { GraphTree, type TreeFocusRequest } from './GraphTree';
import { RELATIONSHIP_COLUMNS, relationshipsFileName, visibleRelationshipRows } from './graphExport';
import { keepsId, pruneViewState } from './graphRefresh';
import { graphTitle } from './graphTitle';
import { GraphUpdateNote } from './GraphUpdateNote';
import { RefreshButton } from './RefreshButton';
import { edgesOf, indexOf, mentionCounts } from './graphIndex';
import {
  branchIds,
  canCollapse,
  childGroups,
  collapseState,
  hiddenBy,
  activationRequest,
  deselectionRequest,
  KIND_FILTERS,
  layoutCentre,
  nodeLabel,
  pathEdgeIds,
  pathToRoot,
  resolveRootId,
  revealAnywhere,
  revealNeighbour,
  rootFromParam,
  selectVisible,
  type GroupViewNode,
  type ViewState,
  type VisibleGraph,
  withNextPage,
} from './graphModel';
import { searchGraph } from './searchGraph';
import { SelectionPanel } from './SelectionPanel';
import { useFullscreen } from './useFullscreen';
import { useGraphRefresh } from './useGraphRefresh';
import { usePageShortcuts } from './usePageShortcuts';
import { useProjectGraph } from './useProjectGraph';
import { useViewUrl } from './useViewUrl';
import type { UrlView, ViewMode } from './viewUrl';

const EMPTY: VisibleGraph = {
  nodes: [],
  edges: [],
  treeEdges: [],
  parentIds: new Set(),
  expandableIds: new Set(),
  parentOf: new Map(),
};
/** A click makes the selected node the centre of the rings; `false` keeps the rings on the focus root. */
const CENTRE_ON_SELECT = true;
const PANEL = 'rounded-lg border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-800';

function toggled<T>(prev: ReadonlySet<T>, value: T): ReadonlySet<T> {
  const next = new Set(prev);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

export default function ProjectGraph() {
  const { id } = useParams<{ id: string }>();
  const graphQuery = useProjectGraph(id);
  const graph = graphQuery.data;
  const title = graphTitle(graph, id);
  useDocumentTitle(title);

  // The view state below is kept in the URL (see useViewUrl), which sets it on arrival.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // `undefined` until a focus is picked, cleared, or named by the URL; until then the default focus applies.
  const [chosenRoot, setChosenRoot] = useState<string | null | undefined>(undefined);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [revealedIds, setRevealedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [pageByGroup, setPageByGroup] = useState<ReadonlyMap<string, number>>(() => new Map());
  const [viewportRequest, setViewportRequest] = useState<ViewportRequest | null>(null);
  const [hiddenKinds, setHiddenKinds] = useState<ReadonlySet<ProjectGraphNodeKind>>(() => new Set());
  const [hiddenCategories, setHiddenCategories] = useState<ReadonlySet<string>>(() => new Set());
  const [mode, setMode] = useState<ViewMode>('graph');
  const [treeFocus, setTreeFocus] = useState<TreeFocusRequest | null>(null);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<GraphCanvasHandle>(null);
  // The canvas and the side panel together, so the selection panel stays usable in full screen.
  const areaRef = useRef<HTMLDivElement>(null);
  const fullscreen = useFullscreen(areaRef);

  const nodesById = useMemo(
    (): ReadonlyMap<string, ProjectGraphNode> => (graph ? indexOf(graph).nodesById : new Map()),
    [graph],
  );
  // The entry node, or the project when the graph has none.
  const defaultRoot = graph ? rootFromParam(graph, null) : null;
  // A chosen focus that a refetch dropped from the graph falls back to the default focus.
  const chosenShown = chosenRoot !== undefined && (chosenRoot === null || nodesById.has(chosenRoot));
  const rootId = chosenShown ? chosenRoot : defaultRoot;

  const viewState: ViewState = useMemo(
    () => ({ rootId, expandedIds, revealedIds, pageByGroup, hiddenKinds, hiddenCategories }),
    [rootId, expandedIds, revealedIds, pageByGroup, hiddenKinds, hiddenCategories],
  );
  // A newer snapshot keeps the view: only the ids that vanished leave the view state and the selection.
  const graphChange = useGraphRefresh(graph, (nodeIds) => {
    const pruned = pruneViewState(viewState, nodeIds);
    setExpandedIds(pruned.expandedIds);
    setRevealedIds(pruned.revealedIds);
    setPageByGroup(pruned.pageByGroup);
    setSelectedId((prev) => (prev !== null && !keepsId(prev, nodeIds) ? null : prev));
  });
  const visible = useMemo(() => (graph ? selectVisible(graph, viewState) : EMPTY), [graph, viewState]);
  const visibleIds = useMemo(() => new Set(visible.nodes.map((n) => n.id)), [visible]);
  // A selected node that left the view (collapsed, filtered, or a new focus) counts as no selection.
  const selection = selectedId !== null && visibleIds.has(selectedId) ? selectedId : null;
  const groups = useMemo(
    () => (graph && selection ? childGroups(graph, viewState, selection) : []),
    [graph, viewState, selection],
  );
  const categories = useMemo(
    () =>
      [
        ...new Set((graph?.nodes ?? []).flatMap((n) => (n.kind === 'document' && n.category ? [n.category] : []))),
      ].sort(),
    [graph],
  );
  const results = useMemo(() => (graph ? searchGraph(graph, query) : []), [graph, query]);
  const path = useMemo(() => (selection === null ? [] : pathToRoot(visible.parentOf, selection)), [visible, selection]);
  const pathEdges = useMemo(() => pathEdgeIds(visible.treeEdges, path), [visible, path]);
  const treeEdgeIds = useMemo(() => new Set(visible.treeEdges.map((e) => e.id)), [visible]);
  const mentions = useMemo(() => (graph ? mentionCounts(indexOf(graph)) : new Map<string, number>()), [graph]);
  // The filter label of each neighbour of the selection that a filter hides: its node type, else its category.
  const hiddenFilters = useMemo(() => {
    const labels = new Map<string, string>();
    if (!graph || selection === null) return labels;
    const root = resolveRootId(graph, viewState.rootId);
    for (const edge of edgesOf(indexOf(graph), selection)) {
      const otherId = edge.from === selection ? edge.to : edge.to === selection ? edge.from : null;
      const other = otherId === null ? undefined : nodesById.get(otherId);
      const by = other ? hiddenBy(other, viewState, root) : null;
      if (!other || by === null) continue;
      const kindLabel = KIND_FILTERS.find((f) => f.kind === other.kind)?.label ?? other.kind;
      const categoryLabel = other.kind === 'document' ? other.category : null;
      labels.set(other.id, by === 'category' && categoryLabel !== null ? categoryLabel : kindLabel);
    }
    return labels;
  }, [graph, nodesById, viewState, selection]);
  const engines = useMemo(() => (graph?.nodes ?? []).filter((n) => n.kind === 'engine'), [graph]);
  const urlGraph = useMemo(
    () => (graph ? { nodeIds: new Set(nodesById.keys()), categories: new Set(categories) } : null),
    [graph, nodesById, categories],
  );
  // The view a URL names (on arrival, Back, Forward). A new focus starts afresh the parts a URL does not carry, as
  // `setRoot` does; the same focus keeps the pages of the groups still open. The filters are the URL's either way.
  // A selected group member past its group's first page is revealed, so the selection shows.
  const applyUrl = (url: UrlView, arrival: boolean) => {
    const focusChanged = (url.rootId === undefined ? defaultRoot : url.rootId) !== rootId;
    const expanded = new Set(url.expandedIds);
    const selected = url.selectedId;
    setChosenRoot(url.rootId);
    setExpandedIds(expanded);
    setRevealedIds((prev) => new Set([...(focusChanged ? [] : prev), ...(selected === null ? [] : [selected])]));
    setPageByGroup((prev) => (focusChanged ? new Map() : new Map([...prev].filter(([g]) => expanded.has(g)))));
    setSelectedId(selected);
    setHiddenKinds(url.hiddenKinds);
    setHiddenCategories(url.hiddenCategories);
    setMode(url.mode);
    // A new focus fits the view, as a focus picked here does; the same focus shows the selection with its ring 1.
    setViewportRequest(arrival || focusChanged || selected === null ? null : { kind: 'centre', nodeId: selected });
  };
  useViewUrl({
    graph: urlGraph,
    view: { ...viewState, rootId: chosenShown ? chosenRoot : undefined },
    // The selection as picked, shown or not: a collapse or a filter that hides it is no new selection, so it adds no
    // history entry, and a link reopens it hidden, as the page keeps it. A refresh drops a vanished id from it.
    selectedId,
    mode,
    onApply: applyUrl,
  });

  // A new focus starts collapsed and keeps the selection only when it is the new focus; the URL follows it.
  const setRoot = (nodeId: string | null) => {
    setChosenRoot(nodeId);
    setExpandedIds(new Set());
    setRevealedIds(new Set());
    setPageByGroup(new Map());
    setSelectedId((prev) => (prev !== null && prev === nodeId ? prev : null));
  };
  // A click (or Enter/Space) selects the node and shows its children; it never collapses. The rings re-centre
  // on the node and the view shows it with its ring 1, or, with `CENTRE_ON_SELECT` off, the node keeps its place
  // on screen. Stable, so the canvas's memoised nodes survive unrelated renders such as search keystrokes.
  const activate = useCallback((nodeId: string) => {
    setSelectedId(nodeId);
    setExpandedIds((prev) => (prev.has(nodeId) ? prev : new Set([...prev, nodeId])));
    setViewportRequest(activationRequest(nodeId, CENTRE_ON_SELECT));
  }, []);
  // From the node's chevron or the sidebar; the node keeps its place on screen. A group collapsed here starts
  // at one page when it opens again. Stable, as `activate` is: it reads the graph and view state of the last
  // commit, so a selection does not hand every collapsible card new data.
  const committed = useRef({ graph, viewState });
  useLayoutEffect(() => {
    committed.current = { graph, viewState };
  });
  const collapse = useCallback((nodeId: string) => {
    const { graph: current, viewState: state } = committed.current;
    if (!current) return;
    const next = collapseState(current, state, nodeId);
    setExpandedIds(next.expandedIds);
    setPageByGroup(next.pageByGroup);
    setViewportRequest({ kind: 'anchor', nodeIds: [nodeId] });
  }, []);
  // From the group card's +30 or the sidebar; the group keeps its place on screen. Stable, as `activate` is.
  const showMore = useCallback((groupId: string) => {
    setPageByGroup((prev) => withNextPage(prev, groupId));
    setViewportRequest({ kind: 'anchor', nodeIds: [groupId] });
  }, []);
  // A neighbour picked in the sidebar is brought into the view `from` gives (the current one unless a filter
  // was just cleared), selected, and centred. One that no state can show (a filter hides it) leaves the view
  // and the selection as they are.
  const showNeighbour = (targetId: string, from: ViewState = viewState) => {
    if (!graph || selection === null) return;
    const next = revealNeighbour(graph, from, selection, targetId);
    const shownIds = from === viewState ? visibleIds : new Set(selectVisible(graph, from).nodes.map((n) => n.id));
    if (next === from && !shownIds.has(targetId)) return;
    setExpandedIds(next.expandedIds);
    setRevealedIds(next.revealedIds);
    setSelectedId(targetId);
    setViewportRequest({ kind: 'centre', nodeId: targetId });
  };
  // The view state without the filter entries that hide `target`. A document hidden by its kind and by its
  // category needs both cleared, or the row would ask for a second click.
  const withoutFiltersOf = (target: ProjectGraphNode): ViewState => {
    const withoutKind = hiddenKinds.has(target.kind) ? toggled(hiddenKinds, target.kind) : hiddenKinds;
    const category = target.kind === 'document' ? target.category : null;
    const withoutCategory =
      category !== null && hiddenCategories.has(category) ? toggled(hiddenCategories, category) : hiddenCategories;
    return { ...viewState, hiddenKinds: withoutKind, hiddenCategories: withoutCategory };
  };
  // Clears every filter entry that hides a neighbour of the selection, then shows it as a pick would.
  const showAndReveal = (targetId: string) => {
    const target = nodesById.get(targetId);
    const by = graph && target ? hiddenBy(target, viewState, resolveRootId(graph, viewState.rootId)) : null;
    if (!target || by === null) return;
    const unfiltered = withoutFiltersOf(target);
    setHiddenKinds(unfiltered.hiddenKinds);
    setHiddenCategories(unfiltered.hiddenCategories);
    showNeighbour(targetId, unfiltered);
  };
  // A node picked anywhere in the graph (the audit card): the filters that hide it are cleared, and it is brought
  // into the view, selected, and centred. One that no visible node leads to becomes the focus, as a search pick does.
  const showNode = (targetId: string) => {
    const target = nodesById.get(targetId);
    if (!graph || !target) return;
    const next = revealAnywhere(graph, withoutFiltersOf(target), targetId);
    if (next === null) {
      chooseFocus(targetId);
      return;
    }
    setHiddenKinds(next.hiddenKinds);
    setHiddenCategories(next.hiddenCategories);
    setExpandedIds(next.expandedIds);
    setRevealedIds(next.revealedIds);
    setSelectedId(targetId);
    setViewportRequest({ kind: 'centre', nodeId: targetId });
  };
  // An ancestor picked in the breadcrumb is selected and centred, as any other pick in the sidebar is.
  const selectAncestor = (nodeId: string) => {
    setSelectedId(nodeId);
    setViewportRequest({ kind: 'centre', nodeId });
  };
  const openGroup = (group: GroupViewNode) => setExpandedIds((prev) => new Set([...prev, group.parentId, group.id]));
  // A group opened from the sidebar keeps its place on screen, or its parent's when it was not shown yet.
  const expandGroup = (group: GroupViewNode) => {
    openGroup(group);
    setViewportRequest({ kind: 'anchor', nodeIds: [group.id, group.parentId] });
  };
  const revealMember = (group: GroupViewNode, memberId: string) => {
    openGroup(group);
    setRevealedIds((prev) => new Set([...prev, memberId]));
    setSelectedId(memberId);
    setViewportRequest({ kind: 'centre', nodeId: memberId });
  };
  // An engine picked as the focus is also selected, so its panel shows.
  const chooseFocus = (nodeId: string | null) => {
    setRoot(nodeId);
    if (nodeId !== null) setSelectedId(nodeId);
  };
  const pick = (nodeId: string) => {
    setRoot(nodeId);
    setSelectedId(nodeId);
    setQuery('');
    if (mode === 'list') setTreeFocus({ nodeId });
  };
  // The tree's Right, `*`, and chevron: the rows' children show; nothing is selected.
  const expand = useCallback(
    (ids: string[]) => setExpandedIds((prev) => (ids.every((i) => prev.has(i)) ? prev : new Set([...prev, ...ids]))),
    [],
  );
  // The canvas, mounted again, shows the selection with its ring 1, as a click on it does. The toggle pressed goes
  // with the toolbar it sat in, so focus moves to the new toolbar's once it is drawn.
  const changeMode = (next: ViewMode) => {
    setMode(next);
    setViewportRequest(next === 'graph' && selection !== null ? { kind: 'centre', nodeId: selection } : null);
    requestAnimationFrame(() =>
      areaRef.current?.querySelector<HTMLElement>('[data-view-toggle] [aria-pressed="true"]')?.focus(),
    );
  };
  const openDocument = (reconstructedDocumentId: string) =>
    window.open(`/reconstructed-documents/${encodeURIComponent(reconstructedDocumentId)}`, '_blank', 'noopener');
  // The relationships the canvas draws, as they stand when the button is pressed; groups are left out.
  const exportCsv = () => {
    if (!graph) return;
    const csv = serializeCsv(visibleRelationshipRows(graph, visible), RELATIONSHIP_COLUMNS);
    downloadText(csv, relationshipsFileName(graph.projectId, new Date()), 'text/csv');
  };
  const fitBranch = (nodeId: string) =>
    setViewportRequest({ kind: 'fit', nodeIds: [...branchIds(visible.parentOf, nodeId)] });
  const viewRootId = graph ? resolveRootId(graph, rootId) : '';
  const centreId = layoutCentre(selection, viewRootId, CENTRE_ON_SELECT);
  // A click on the empty canvas, or Escape: the rings return to the root, and the view shows it with its ring 1.
  // With `CENTRE_ON_SELECT` off the rings never left the root, so the view stays.
  const deselect = () => {
    if (selection === null) return;
    setSelectedId(null);
    const request = deselectionRequest(viewRootId, CENTRE_ON_SELECT);
    if (request) setViewportRequest(request);
  };
  const selectedNode = selection === null ? undefined : visible.nodes.find((n) => n.id === selection);
  const selectionCollapsible =
    selectedNode !== undefined && canCollapse(selectedNode, viewRootId, expandedIds, visible.parentIds);
  const focusNode = rootId ? nodesById.get(rootId) : undefined;
  const entryNode = graph?.entryNodeId ? nodesById.get(graph.entryNodeId) : undefined;
  const workingEngineId = entryNode?.kind === 'engine' ? entryNode.id : null;
  // After the menu closes: it hands focus back to its button first, which would take it from the search box.
  const focusSearch = () => requestAnimationFrame(() => searchRef.current?.focus());
  const focusLabel = focusNode ? nodeLabel(focusNode) : 'Whole project';
  // A retry of a failed first load puts the query back to pending, which would swap the error for the skeleton;
  // the error stays, its Retry spinning, until the retry settles.
  const loadFailed = graphQuery.isError || (graph === undefined && graphQuery.errorUpdateCount > 0);
  // The root always shows, so the view is empty when it shows nothing else.
  const filtersOn = hiddenKinds.size > 0 || hiddenCategories.size > 0;
  const empty = graph ? emptyState(graph, visible.nodes.length - 1, filtersOn) : null;
  const canvasShown = graph !== undefined && empty === null && mode === 'graph';
  // A canvas mounted again starts with no request handled, so it would replay the last one against positions it
  // never drew; a hidden canvas (an empty view, or the list mode) has no request pending.
  const [canvasWasShown, setCanvasWasShown] = useState(canvasShown);
  if (canvasShown !== canvasWasShown) {
    setCanvasWasShown(canvasShown);
    if (!canvasShown) setViewportRequest(null);
  }
  const clearFilters = () => {
    setHiddenKinds(new Set());
    setHiddenCategories(new Set());
    setViewportRequest(null);
  };
  const fullscreenControl = fullscreen.enabled ? { active: fullscreen.active, onToggle: fullscreen.toggle } : undefined;
  usePageShortcuts({
    search: () => searchRef.current?.focus(),
    centre: mode === 'graph' ? () => canvasRef.current?.centreOnFocus() : null,
    fullscreen: fullscreen.enabled ? fullscreen.toggle : null,
  });

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={title}
        description={
          <>
            Entities and the documents that mention them
            {id && (
              <>
                {' · '}
                <Link to={`/projects/${encodeURIComponent(id)}`} className="whitespace-nowrap">
                  Project {formatShortId(id)}
                </Link>
              </>
            )}
            {graph && (
              <>
                {' · '}
                <GeneratedAt at={graph.generatedAt} />
                {/* The view state lives outside the query, so the focus, expansion, and selection survive it. */}
                <RefreshButton fetching={graphQuery.isFetching} onRefresh={() => void graphQuery.refetch()} />
              </>
            )}
          </>
        }
        icon={<ShareIcon className="size-6" />}
        showBackButton={true}
      >
        {id && graph && (
          <GraphTaskMenu projectId={id} engineId={workingEngineId} onFocus={chooseFocus} onFind={focusSearch} />
        )}
      </PageHeader>
      <GraphUpdateNote change={graphChange} />
      {graphQuery.isLoading && !loadFailed && <TableSkeleton />}
      {loadFailed && (
        <GraphLoadError retrying={graphQuery.isFetching} onRetry={() => void graphQuery.refetch()} />
      )}
      {/* A project without documents, or without entities, has nothing to search, focus, or filter. */}
      {graph && (empty === 'noDocuments' || empty === 'noEntities') && <GraphEmptyState state={empty} />}
      {graph && (empty === null || empty === 'filtered') && (
        <div
          ref={areaRef}
          // The browser paints black behind a full-screen element without a background of its own.
          className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row [&:fullscreen]:overflow-y-auto [&:fullscreen]:bg-white [&:fullscreen]:p-4 dark:[&:fullscreen]:bg-zinc-900"
        >
          {empty === 'filtered' ? (
            <GraphEmptyState state={empty} onClearFilters={clearFilters} />
          ) : mode === 'list' ? (
            <GraphTree
              visible={visible}
              expandedIds={expandedIds}
              selectedId={selection}
              mentionCounts={mentions}
              focusRequest={treeFocus}
              onActivate={activate}
              onExpand={expand}
              onCollapse={collapse}
              onShowMore={showMore}
              onExportCsv={exportCsv}
              onMode={changeMode}
              fullscreen={fullscreenControl}
            />
          ) : (
            <GraphCanvas
              ref={canvasRef}
              nodes={visible.nodes}
              edges={visible.edges}
              treeEdgeIds={treeEdgeIds}
              parentIds={visible.parentIds}
              mentionCounts={mentions}
              pathEdgeIds={pathEdges}
              rootId={viewRootId}
              centreId={centreId}
              selectedId={selection}
              expandedIds={expandedIds}
              expandableIds={visible.expandableIds}
              viewportRequest={viewportRequest}
              onActivate={activate}
              onCollapse={collapse}
              onShowMore={showMore}
              onDeselect={deselect}
              onOpenDocument={openDocument}
              onExportCsv={exportCsv}
              onMode={changeMode}
              fullscreen={fullscreenControl}
            />
          )}
          <aside className="flex min-w-0 flex-col gap-4 lg:w-80 lg:shrink-0 lg:overflow-y-auto">
            <div className={PANEL}>
              <GraphSearch query={query} onQuery={setQuery} results={results} onPick={pick} inputRef={searchRef} />
            </div>
            <div className={PANEL}>
              <FocusPicker
                engines={engines}
                entryNodeId={graph.entryNodeId}
                rootId={rootId}
                rootLabel={focusLabel}
                onChange={chooseFocus}
              />
            </div>
            <div className={PANEL}>
              <SelectionPanel
                graph={graph}
                nodesById={nodesById}
                visibleNodes={visible.nodes}
                selectedId={selection}
                path={path}
                hiddenFilters={hiddenFilters}
                focused={selection !== null && rootId === selection}
                childGroups={groups}
                collapsible={selectionCollapsible}
                onFitBranch={fitBranch}
                onCollapse={collapse}
                onFocus={setRoot}
                onSelect={(targetId) => showNeighbour(targetId)}
                onSelectAncestor={selectAncestor}
                onShowAndReveal={showAndReveal}
                onShowNode={showNode}
                onExpandGroup={expandGroup}
                onRevealMember={revealMember}
                onShowMore={showMore}
              />
            </div>
            <div className={PANEL}>
              <GraphFilters
                hiddenKinds={hiddenKinds}
                onToggleKind={(kind) => setHiddenKinds((prev) => toggled(prev, kind))}
                categories={categories}
                hiddenCategories={hiddenCategories}
                onToggleCategory={(category) => setHiddenCategories((prev) => toggled(prev, category))}
              />
            </div>
            <GraphAudit graph={graph} nodesById={nodesById} onSelect={showNode} />
          </aside>
        </div>
      )}
    </div>
  );
}
